import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Returns whether the backend currently allows writes back to FileMaker.
 * Reads once per app mount from GET /api/fm/write-status; result is cached in
 * module scope so multiple hook callers share the same fetch.
 *
 * When FileMaker writes are disabled (default in 2026-02+), the Web App is a
 * READ-ONLY consumer of the FileMaker product database: no drawer Edit, no
 * variant PRICE inline edit, no photo upload, no create-new-product.
 * The status can be flipped back on for emergency maintenance by setting the
 * `FILEMAKER_WRITES_ENABLED=1` env var on the backend.
 */
let cachedStatus = null; // null = unknown; boolean = fetched
let inflight = null;

export function useFmWritesEnabled() {
  const [enabled, setEnabled] = useState(cachedStatus);

  useEffect(() => {
    if (cachedStatus !== null) {
      setEnabled(cachedStatus);
      return;
    }
    if (!inflight) {
      inflight = api
        .get("/fm/write-status")
        .then(({ data }) => {
          cachedStatus = !!data?.writes_enabled;
          return cachedStatus;
        })
        .catch(() => {
          // On error assume writes are DISABLED (safest default for a
          // read-only sync guarantee - never surface Edit UI unless we've
          // actually confirmed the backend allows it).
          cachedStatus = false;
          return false;
        })
        .finally(() => {
          inflight = null;
        });
    }
    inflight.then((v) => setEnabled(v));
  }, []);

  // While unknown, return `false` so no write UI is shown until we've confirmed
  // the backend permits it.
  return enabled === true;
}
